import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import { Modal, Input, Table, Spin, Tooltip } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import action from '@flux/action';
import { parseBulkEmails } from '../utils/parseBulkEmails';
import {
  validatePwcEmails,
  validateClientEmails,
  buildInvalidFormatRows,
} from '../utils/bulkAddUsers';
import {
  getBulkAddReportIssuePage,
  getBulkAddSaveHint,
  formatPwcEmployeeTooltip,
} from '../utils/bulkAddUsers.ui';
import './BulkAddUsersModal.scss';

const { TextArea } = Input;

const STATUS_LABELS = {
  valid: 'Valid',
  invalid: 'Invalid',
  skipped: 'Skipped',
};

const StatusBadge = ({ status }) => (
  <span className={`bulk-add-status bulk-add-status--${status}`}>
    {STATUS_LABELS[status]}
  </span>
);

const SummaryStats = ({ validCount, invalidCount, skippedCount }) => (
  <div className="bulk-add-summary-stats">
    <div className="bulk-add-stat bulk-add-stat--valid">
      <CheckCircleFilled className="bulk-add-stat__icon" />
      <span className="bulk-add-stat__count">{validCount}</span>
      <span className="bulk-add-stat__label">Valid</span>
    </div>
    <div className="bulk-add-stat bulk-add-stat--invalid">
      <CloseCircleFilled className="bulk-add-stat__icon" />
      <span className="bulk-add-stat__count">{invalidCount}</span>
      <span className="bulk-add-stat__label">Invalid</span>
    </div>
    <div className="bulk-add-stat bulk-add-stat--skipped">
      <MinusCircleFilled className="bulk-add-stat__icon" />
      <span className="bulk-add-stat__count">{skippedCount}</span>
      <span className="bulk-add-stat__label">Skipped</span>
    </div>
  </div>
);

const BulkAddUsersModal = ({
  visible,
  teamType = 'pwc',
  existingUsers = [],
  createStep = false,
  onConfirm,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState('input');
  const [emailInput, setEmailInput] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState('');
  const tableWrapRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(0);

  const resetState = useCallback(() => {
    setStep('input');
    setEmailInput('');
    setPreviewRows([]);
    setValidating(false);
    setValidationProgress('');
  }, []);

  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible, resetState]);

  useLayoutEffect(() => {
    if (step !== 'preview') {
      setTableScrollY(0);
      return undefined;
    }

    if (!tableWrapRef.current) {
      return undefined;
    }

    const updateTableScroll = () => {
      const container = tableWrapRef.current;
      if (!container) {
        return;
      }

      const header =
        container.querySelector('.ant-table-header') ||
        container.querySelector('.ant-table-thead');
      const headerHeight = header ? header.offsetHeight : 0;
      const bodyHeight = container.clientHeight - headerHeight;

      setTableScrollY((prev) => {
        const next = Math.max(bodyHeight, 80);
        return prev === next ? prev : next;
      });
    };

    updateTableScroll();
    const rafId = requestAnimationFrame(() => {
      updateTableScroll();
      requestAnimationFrame(updateTableScroll);
    });

    const observer = new ResizeObserver(updateTableScroll);
    observer.observe(tableWrapRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [step, previewRows.length, visible]);

  const handleReportIssue = () => {
    dispatch(
      action.popReportIssueModal({
        connectedPage: getBulkAddReportIssuePage(createStep),
        category: "I can't add users",
      }),
    );
  };

  const handleValidate = async () => {
    const { validFormat, invalidFormat } = parseBulkEmails(emailInput);

    if (validFormat.length === 0 && invalidFormat.length === 0) {
      return;
    }

    setValidating(true);
    setValidationProgress(
      `Validating ${validFormat.length} email${
        validFormat.length !== 1 ? 's' : ''
      }...`,
    );

    const invalidRows = buildInvalidFormatRows(invalidFormat);

    let apiRows = [];
    if (validFormat.length > 0) {
      if (teamType === 'pwc') {
        apiRows = await validatePwcEmails(validFormat, existingUsers);
      } else {
        apiRows = await validateClientEmails(validFormat, existingUsers);
      }
    }

    setPreviewRows([...apiRows, ...invalidRows]);
    setStep('preview');
    setValidating(false);
    setValidationProgress('');
  };

  const handleConfirm = () => {
    const validRows = previewRows.filter((row) => row.status === 'valid');
    if (validRows.length > 0) {
      onConfirm(validRows);
    }
    resetState();
    onCancel();
  };

  const handleBack = () => {
    setStep('input');
    setPreviewRows([]);
  };

  const validCount = previewRows.filter((r) => r.status === 'valid').length;
  const invalidCount = previewRows.filter((r) => r.status === 'invalid').length;
  const skippedCount = previewRows.filter((r) => r.status === 'skipped').length;

  const parsed = parseBulkEmails(emailInput);
  const emailCount = parsed.validFormat.length + parsed.invalidFormat.length;
  const isInputEmpty = emailCount === 0;

  const previewColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '36%',
      ellipsis: true,
      render: (email) => <span className="bulk-add-email-cell">{email}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '16%',
      align: 'center',
      render: (status) => <StatusBadge status={status} />,
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details, record) => {
        const employeeTooltip =
          teamType === 'pwc' && record.status === 'valid' && record.memberData
            ? formatPwcEmployeeTooltip(record.memberData)
            : null;

        return (
          <div className="bulk-add-details-cell">
            <span className="bulk-add-details-text">
              {details}
              {teamType === 'pwc' &&
                record.status === 'invalid' &&
                record.reason === 'Not found in PwC directory' && (
                  <>
                    {' '}
                    <span
                      className="bulk-add-report-link"
                      role="button"
                      tabIndex={0}
                      onClick={handleReportIssue}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleReportIssue();
                        }
                      }}
                    >
                      Report an issue
                    </span>
                  </>
                )}
            </span>
            {employeeTooltip && (
              <Tooltip title={employeeTooltip} placement="left">
                <InfoCircleOutlined
                  className="bulk-add-details-info-icon"
                  aria-label="Employee details"
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const modalTitle = (
    <div className="bulk-add-modal-title">
      <span className="bulk-add-modal-title__text">Bulk Add Users</span>
      <span className="bulk-add-modal-title__step">
        {step === 'input'
          ? 'Step 1 of 2 — Enter email addresses'
          : 'Step 2 of 2 — Review and confirm'}
      </span>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      maskClosable={false}
      destroyOnClose
      width={760}
      centered
      className="bulk-add-users-modal"
      wrapClassName="bulk-add-users-modal-wrap"
      bodyStyle={{
        height: step === 'input' ? 250 : 400,
        overflow: 'hidden',
        padding: '20px 24px',
      }}
      onCancel={() => {
        if (step === 'preview') {
          handleBack();
          return;
        }
        resetState();
        onCancel();
      }}
      okText={
        step === 'input'
          ? 'Preview'
          : `Add ${validCount} User${validCount !== 1 ? 's' : ''}`
      }
      okButtonProps={{
        disabled:
          step === 'input' ? isInputEmpty || validating : validCount === 0,
        loading: validating,
        className: 'bulk-add-ok-btn',
      }}
      cancelButtonProps={{ className: 'bulk-add-cancel-btn' }}
      cancelText={step === 'preview' ? 'Back' : 'Cancel'}
      onOk={step === 'input' ? handleValidate : handleConfirm}
    >
      {step === 'input' && (
        <div className="bulk-add-input-step">
          <p className="bulk-add-users-helper">
            Paste or type email addresses separated by commas, semicolons, or new
            lines.
          </p>
          <div className="bulk-add-textarea-wrap">
            <TextArea
              className="bulk-add-textarea"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder={
                'user1@company.com, user2@company.com\nuser3@company.com'
              }
              disabled={validating}
            />
          </div>
          <div className="bulk-add-input-meta">
            {emailCount > 0 ? (
              <span className="bulk-add-users-count">
                {emailCount} email{emailCount !== 1 ? 's' : ''} detected
              </span>
            ) : (
              <span className="bulk-add-input-example">
                Example: alice@client.com, bob@client.com
              </span>
            )}
          </div>
          {validating && (
            <div className="bulk-add-users-loading">
              <Spin size="small" />
              <span>{validationProgress}</span>
            </div>
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="bulk-add-preview-step">
          <SummaryStats
            validCount={validCount}
            invalidCount={invalidCount}
            skippedCount={skippedCount}
          />
          <div className="bulk-add-users-preview-table" ref={tableWrapRef}>
            <Table
              className="bulk-add-users-table"
              columns={previewColumns}
              dataSource={previewRows.map((row, index) => ({
                ...row,
                key: `${row.email}-${index}`,
              }))}
              pagination={false}
              size="middle"
              bordered={false}
              scroll={tableScrollY > 0 ? { y: tableScrollY } : undefined}
              rowClassName={(record) =>
                `bulk-add-preview-row bulk-add-preview-row--${record.status}`
              }
              locale={{ emptyText: 'No emails to preview' }}
            />
          </div>
          {validCount > 0 ? (
            <p className="bulk-add-users-save-hint">
              {getBulkAddSaveHint(createStep)}
            </p>
          ) : (
            <p className="bulk-add-users-save-hint bulk-add-users-save-hint--warning">
              No valid emails to add. Go back to fix your list or try different
              addresses.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BulkAddUsersModal;
